import React from "react";
import {
  clearActionBusinessInvitation,
  requestActionBusinessInvitation,
  requestListBusinessInvitation,
  useAuthState,
  useBusinessInvitationDispatch,
  useBusinessInvitationState,
} from "../../Context";
import { useEffect } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import TableSkeleton from "../../Components/TableSkeleton";
import Badge from "../../Components/Badge";
import { Pagination } from "../../Components/Pagination";
import { cancelAPIRequest, classNames, isLastTokenNull } from "../../Utils";
import { CheckIcon, SelectorIcon } from "@heroicons/react/outline";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { Fragment } from "react";
import { Button } from "../../Components/Button";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { Alert } from "../../Components/Alert";
import { ModalAlert } from "../../Components/ModalAlert";
import ErrorStateTable from "../../Components/ErrorStateTable";

const statusLabels = [
  { label: "Invited", value: "INVITED" },
  { label: "Accepted", value: "ACCEPTED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Canceled", value: "CANCELLED" },
  { label: "Expired", value: "EXPIRED" },
];

const BusinessInvitationList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useBusinessInvitationDispatch();
  const {
    request,
    businessInvites,
    businessInvitesLoading,
    businessInvitesError,
    businessInvite,
    businessInviteLoading,
    businessInviteError,
    nextTokens,
  } = useBusinessInvitationState();
  const { user } = useAuthState();

  const [showRejectModal, setshowRejectModal] = useState(false);
  const [showCancelModal, setshowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [status, setstatus] = useState(statusLabels[0]);
  const [page, setpage] = useState(1);
  const [businessInviteId, setbusinessInviteId] = useState(null);

  const statusBadgeMap = {
    EXPIRED: { type: "secondary" },
    ACCEPTED: { type: "success" },
    INVITED: { type: "alert" },
    REJECTED: { type: "danger" },
    CANCELLED: { type: "danger" },
  };

  useEffect(() => {
    if (request) {
      cancelAPIRequest(request);
    }
    if (status?.value) {
      requestListBusinessInvitation(dispatch, {
        page: 1,
        status: status?.value,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, status?.value]);

  useEffect(() => {
    return () => {
      clearActionBusinessInvitation(dispatch);
    };
  }, [dispatch]);

  useEffect(() => {
    if (businessInvite?.status) {
      setShowSuccessModal(true);
    }
  }, [businessInvite?.status]);

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListBusinessInvitation(dispatch, {
        page: nextPage,
        status: status.value,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListBusinessInvitation(dispatch, {
        page: prevPage,
        status: status.value,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListBusinessInvitation(dispatch, {
        page: selectedPage,
        status: status.value,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  const handleCancelConfirmation = () => {
    setbusinessInviteId(null);
    if (showCancelModal) {
      requestActionBusinessInvitation(dispatch, {
        invite_id: businessInviteId,
        action: "CANCEL",
      });
      setshowCancelModal(false);
    } else {
      requestActionBusinessInvitation(dispatch, {
        invite_id: businessInviteId,
        action: "REJECT",
      });
      setshowRejectModal(false);
    }
  };

  return (
    <div className="space-y-4">
      {businessInvite?.id && businessInvite?.status && (
        <ModalConfirm
          open={showSuccessModal}
          type="SUCCESS"
          setOpen={(open) => {
            setShowSuccessModal(open);
          }}
          title="Berhasil"
          description={`Business access invitation was successfully ${businessInvite?.status?.toLowerCase()}`}
          onConfirm={() => {
            setShowSuccessModal(false);
            clearActionBusinessInvitation(dispatch);
          }}
        />
      )}
      {(showCancelModal || showRejectModal) && (
        <ModalAlert
          open={showCancelModal || showRejectModal}
          setOpen={(open) => {
            setshowCancelModal(open);
            setshowRejectModal(open);
          }}
          title={
            showCancelModal ? "Cancel the invitation" : "Reject the invitation"
          }
          description={
            showCancelModal
              ? "Are you sure you want to cancel this invitation?"
              : "Are you sure you want to reject this invitation?"
          }
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          onConfirm={() => handleCancelConfirmation()}
          onCancel={() => {
            setshowCancelModal(false);
            setshowRejectModal(false);
          }}
        />
      )}
      <div className="flex gap-4 justify-between flex-wrap">
        <div>
          <Listbox value={status} onChange={setstatus}>
            {({ open }) => (
              <>
                <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm">
                  <span className="ml-3 mr-2">
                    {t(status?.label || t("Select Status"))}
                  </span>
                  <SelectorIcon className="w-5 h-5 text-gray-500" />
                </ListboxButton>
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                    {statusLabels.map((item) => (
                      <ListboxOption
                        key={item.value}
                        className={({ focus }) =>
                          classNames(
                            focus ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-3 pr-9"
                          )
                        }
                        value={item}
                      >
                        {({ selected, focus }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "ml-3 mr-2 block truncate"
                                )}
                              >
                                {t(item.label)}
                              </span>
                              {selected ? (
                                <span
                                  className={classNames(
                                    focus ? "text-white" : "text-blue-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
        <Button
          title="New Invitation"
          onClick={() => navigate("/business-invites/create")}
        />
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              {businessInviteError && (
                <div className="bg-white px-4 py-6 sm:px-6">
                  <Alert
                    title="Error"
                    type="error"
                    message={
                      (businessInviteError?.response?.body &&
                        JSON.parse(businessInviteError.response.body)
                          ?.message) ||
                      "submitError delete Transaction."
                    }
                    errors={businessInviteError?.errors}
                  />
                </div>
              )}
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Business Name")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("User")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Status")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Expired")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>
                {businessInvitesLoading ? (
                  <TableSkeleton rows={3} columns={5} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {businessInvites.filter(
                      (item) => item.status === status.value
                    ).length > 0 ? (
                      businessInvites
                        .filter((item) => item.status === status.value)
                        .map((item) => {
                          return (
                            <tr key={item.id}>
                              <td className="px-4 py-2 text-sm text-gray-00 whitespace-nowrap ">
                                {item.currentBusinessName}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-00 whitespace-nowrap">
                                {item?.userID === user?.id &&
                                  item.receiverUser?.name}
                                {item?.receiverUserID === user?.id &&
                                  item.user?.name}
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                <Badge
                                  title={
                                    statusLabels.find(
                                      (label) => label.value === item.status
                                    )?.label
                                  }
                                  type={statusBadgeMap[item.status]?.type}
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {dayjs(item.expiredAt)
                                  .locale(i18n.language)
                                  .format("DD MMM YYYY")}
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {item.status === "INVITED" &&
                                  item.userID === user.id && (
                                    <Button
                                      title="Cancel"
                                      color="red"
                                      loading={businessInviteLoading}
                                      disabled={businessInviteLoading}
                                      onClick={() => {
                                        setshowCancelModal(true);
                                        setbusinessInviteId(item.id);
                                      }}
                                    />
                                  )}
                                {item.status === "INVITED" &&
                                  item.receiverUserID === user.id && (
                                    <div className="flex gap-4">
                                      <Button
                                        title="Reject"
                                        color="red"
                                        loading={businessInviteLoading}
                                        disabled={businessInviteLoading}
                                        onClick={() => {
                                          setshowRejectModal(true);
                                          setbusinessInviteId(item.id);
                                        }}
                                      />
                                      <Button
                                        loading={businessInviteLoading}
                                        disabled={businessInviteLoading}
                                        title="Accept"
                                        onClick={() => {
                                          requestActionBusinessInvitation(
                                            dispatch,
                                            {
                                              invite_id: item.id,
                                              action: "ACCEPT",
                                            }
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      <ErrorStateTable
                        colSpan={12}
                        error={businessInvitesError}
                        emptyTitle={
                          isLastTokenNull(nextTokens)
                            ? "No Invites"
                            : "Please click Next again to load the data"
                        }
                      />
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessInvitationList;
